import { useState } from 'react';
import { api, uploadMedia } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAction, useApi } from '../lib/useApi';
import { Recorder } from '../components/Recorder';
import { LicenceTag, LICENCE_OPTIONS, ProvenanceTag, SensitivityTag } from '../components/Provenance';
import { DialectRow } from '../components/DialectRow';
import { VoicePlayer } from '../components/VoicePlayer';
import { useFamilyTerms } from '../lib/terms';
import { LanguageBadge, LanguageNote, LanguageSelect } from '../components/LanguagePicker';
import { type FamilyLanguage, textProps, useLanguageCatalogue } from '../lib/languages';
import { Empty, ErrorBox, Field, FormActions, Loading, Notice, PageHeader, StatusBadge, fmtDate, useDraft, LoadFailed } from '../components/ui';

const TYPES = [
  { key: 'story', label: 'Story', termKey: 'type.story' },
  { key: 'proverb', label: 'Proverb', termKey: 'type.proverb' },
  { key: 'oriki', label: 'Praise poetry', termKey: 'type.oriki' },
  { key: 'recipe', label: 'Recipe', termKey: 'type.recipe' },
  { key: 'song', label: 'Song', termKey: 'type.song' },
];
const PIPELINE = ['recorded', 'ai_transcribed', 'ai_translated', 'native_review', 'approved'];

interface StoryListItem {
  id: string;
  title: string;
  type: string;
  language_code: string;
  review_status: string;
  ai_assisted: boolean;
  teller_name: string | null;
  excerpt: string;
  created_at: string;
  media_asset_id: string | null;
  provenance: string | null;
  licence: string | null;
  sensitivity: string | null;
  dialect_code: string | null;
  dialect_name: string | null;
}
interface StoryFull extends StoryListItem {
  raw_transcript: string | null;
  transcript: string | null;
  translation: string | null;
}

export function Pipeline({ status }: { status: string }) {
  const at = PIPELINE.indexOf(status);
  return (
    <div className="pipeline" aria-label={`Review status: ${status.replace(/_/g, ' ')}`}>
      {PIPELINE.map((s, i) => (
        <span key={s} className={i < at ? 'done' : i === at ? 'current' : ''}>
          {s.replace('ai_', 'AI ').replace('_', ' ')}
        </span>
      ))}
    </div>
  );
}

export function StoryVaultPage() {
  const { user } = useAuth();
  const { term, primary } = useFamilyTerms();
  const [filter, setFilter] = useState('');
  const list = useApi<{ stories: StoryListItem[] }>(`/stories${filter ? `?type=${filter}` : ''}`);
  const tree = useApi<{ people: Array<{ id: string; display_name: string; relation_label: string | null; child_profile_id: string | null }> }>(user?.role === 'child' ? null : '/tree');
  const [openId, setOpenId] = useState<string | null>(null);
  const canWrite = user?.role === 'parent' || user?.role === 'grandparent' || user?.role === 'teen';

  return (
    <>
      <PageHeader title="Story Vault" termKey="page.story_vault" lead="Proverbs, oríkì, recipes, songs and stories in the voices of the people who carry them. Private to your family." />
      <div className="tabs" role="tablist" aria-label="Filter by type">
        <button role="tab" aria-selected={filter === ''} onClick={() => setFilter('')}>All</button>
        {TYPES.map((t) => (
          <button key={t.key} role="tab" aria-selected={filter === t.key} onClick={() => setFilter(t.key)}>
            {t.label}
            {term(t.termKey) && (
              <span className="yo" lang={primary?.code}>
                {' '}
                · {term(t.termKey)}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="grid grid-2">
        <section className="stack" aria-label="Stories">
          {list.loading && <Loading />}
          <ErrorBox error={list.error} />
          {list.data?.stories.length === 0 && <Empty>No stories yet. Record the first one!</Empty>}
          {list.data?.stories.map((s) => (
            <article key={s.id} className="card stack">
              <div className="spread">
                <h3 className="yo" style={{ margin: 0 }}>{s.title}</h3>
                <span className="badge earth">{TYPES.find((t) => t.key === s.type)?.label}</span>
              </div>
              <p className="small muted" style={{ margin: 0 }}>
                {s.teller_name ? <span className="yo">{s.teller_name}</span> : 'Family'} · {fmtDate(s.created_at, false)}
                {s.dialect_name ? <> · <span className="yo">{s.dialect_name}</span></> : null}
              </p>
              <div className="row" style={{ gap: 6 }}>
                <ProvenanceTag provenance={s.provenance} aiAssisted={s.ai_assisted} />
                <SensitivityTag sensitivity={s.sensitivity} />
                <LicenceTag licence={s.licence} />
              </div>
              {s.excerpt && (
                <p className="yo" {...textProps(s.language_code)}>
                  {s.excerpt}…
                </p>
              )}
              <Pipeline status={s.review_status} />
              <button className="btn small secondary" onClick={() => setOpenId(openId === s.id ? null : s.id)} aria-expanded={openId === s.id}>
                {openId === s.id ? 'Close' : 'Open'}
              </button>
              {openId === s.id && <StoryDetail id={s.id} onChange={list.reload} />}
            </article>
          ))}
        </section>
        {canWrite && <NewStory people={(tree.data?.people ?? []).filter((p) => !p.child_profile_id)} onCreated={list.reload} />}
      </div>
    </>
  );
}

function NewStory({ people, onCreated }: { people: Array<{ id: string; display_name: string; relation_label: string | null }>; onCreated: () => void }) {
  const act = useAction();
  const { term } = useFamilyTerms();
  // Draft-backed, because this is the form where someone may have spent ten
  // minutes writing down what their grandmother said. A closed tab, a flat
  // battery or a mis-tap must not take that with it.
  const [form, setForm, clearDraft] = useDraft('story', {
    title: '', type: 'story', personId: '', rawTranscript: '', languageCode: '', dialectCode: '', licence: 'family_only', sensitivity: 'ordinary',
  });
  const [blob, setBlob] = useState<Blob | null>(null);
  const home = useApi<{ languages: FamilyLanguage[] }>('/family/languages');
  const { byCode } = useLanguageCatalogue();

  // Default to the family's main home language; they can pick any other from the catalogue.
  const homePrimary = home.data?.languages.find((l) => l.is_primary)?.code ?? home.data?.languages[0]?.code ?? 'yo';
  const languageCode = form.languageCode || homePrimary;
  const language = byCode.get(languageCode);

  // If we could not reach Ìran, saving will fail. Say so BEFORE someone records
  // four minutes of their grandmother, rather than after. The recording itself is
  // kept in the page either way, so a failed save loses the upload, never the audio.
  const offline = !!home.error;

  return (
    <form
      className="card stack"
      onSubmit={async (e) => {
        e.preventDefault();
        const ok = await act.run(async () => {
          const mediaAssetId = blob ? await uploadMedia(blob, 'story') : undefined;
          await api('/stories', {
            body: {
              title: form.title,
              type: form.type,
              languageCode,
              personId: form.personId || undefined,
              mediaAssetId,
              rawTranscript: form.rawTranscript || undefined,
              dialectCode: form.dialectCode || undefined,
              licence: form.licence,
              sensitivity: form.sensitivity,
              // A recording is the person's own voice; a typed-only entry is the family's telling.
              provenance: mediaAssetId ? 'family_recording' : 'family_told',
            },
          });
          return true;
        }, 'Saved. Next: add a transcript, then a family member checks it.');
        if (ok) {
          setForm({ ...form, title: '', personId: '', rawTranscript: '', languageCode });
          setBlob(null);
          onCreated();
        }
      }}
    >
      <h2>Record a new story</h2>
      <Field id="st" label="Title">
        <input id="st" className="yo" {...textProps(languageCode)} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
      </Field>
      <Field id="lang" label="Language of the recording" hint="Any language on the list works for the vault — the elder speaks, we keep it.">
        <LanguageSelect id="lang" label="Language of the recording" value={languageCode} onChange={(code) => setForm({ ...form, languageCode: code })} />
      </Field>
      {language && (
        <div className="stack" style={{ gap: 8 }}>
          <div>
            <LanguageBadge language={language} />
          </div>
          <LanguageNote language={language} />
        </div>
      )}
      <div className="row">
        <select aria-label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
          {TYPES.map((t) => (
            <option key={t.key} value={t.key}>
              {term(t.termKey) ? `${t.label} · ${term(t.termKey)}` : t.label}
            </option>
          ))}
        </select>
        <select aria-label="Who is telling it?" value={form.personId} onChange={(e) => setForm({ ...form, personId: e.target.value })}>
          <option value="">Who is telling it?</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.relation_label} {p.display_name}
            </option>
          ))}
        </select>
      </div>
      <DialectRow languageCode={languageCode} value={form.dialectCode} onChange={(dialectCode) => setForm({ ...form, dialectCode })} />
      <div className="row">
        <label className="stack tight" style={{ flex: '1 1 12rem' }}>
          <span className="label">Who may hear this</span>
          <select value={form.licence} onChange={(e) => setForm({ ...form, licence: e.target.value })}>
            {LICENCE_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="stack tight" style={{ flex: '1 1 12rem' }}>
          <span className="label">Is any of it restricted?</span>
          <select
            value={form.sensitivity}
            onChange={(e) => {
              const sensitivity = e.target.value;
              // Restricted knowledge stays inside the family, so the licence follows.
              const licence = sensitivity === 'restricted' && !['family_only', 'extended_family'].includes(form.licence) ? 'family_only' : form.licence;
              setForm({ ...form, sensitivity, licence });
            }}
          >
            <option value="ordinary">No — anyone in the family</option>
            <option value="adults_only">Adults only</option>
            <option value="restricted">Restricted — sacred, or for elders</option>
          </select>
        </label>
      </div>
      {form.sensitivity === 'restricted' && (
        <p className="muted small">
          Ìran will keep this from children and teenagers, and will never send it to an AI provider — not for transcription, not for translation, whatever else your
          family has agreed to.
        </p>
      )}
      <div className="stack">
        <span className="label">Recording</span>
        <Recorder onRecorded={(b) => setBlob(b)} label="Record" />
        <label className="small">
          or upload audio: <input type="file" accept="audio/*" onChange={(e) => setBlob(e.target.files?.[0] ?? null)} />
        </label>
        {blob && <audio controls src={URL.createObjectURL(blob)} aria-label="Preview recording" />}
      </div>
      <Field id="rt" label="Rough transcript (optional)" hint="Type what you hear, or paste speech-to-text. AI can tidy tone marks later; a family member always checks.">
        <textarea id="rt" className="yo" {...textProps(languageCode)} value={form.rawTranscript} onChange={(e) => setForm({ ...form, rawTranscript: e.target.value })} />
      </Field>
      <ErrorBox error={act.error} />
      <Notice text={act.notice} />
      {offline && (
        <p className="alert safety" role="status">
          <strong>We cannot reach Ìran at the moment, so this cannot be saved yet.</strong> If you have already recorded something it is still here — leave this page
          open and press Save when you are back online. Nothing you have recorded is lost.
        </p>
      )}
      <FormActions
        submitLabel="Save to the vault"
        busy={act.busy}
        disabled={offline}
        onClear={() => {
          clearDraft();
          setBlob(null);
        }}
        hint={form.rawTranscript.length > 0 ? 'Your typing is kept on this device until you save it.' : undefined}
      />
    </form>
  );
}

function StoryDetail({ id, onChange }: { id: string; onChange: () => void }) {
  const { user } = useAuth();
  const { data, error, loading, reload } = useApi<{ story: StoryFull; history: Array<{ to_status: string; actor: string; created_at: string }> }>(`/stories/${id}`);
  const act = useAction();
  const { byCode } = useLanguageCatalogue();
  const [edit, setEdit] = useState<{ transcript: string; translation: string } | null>(null);
  if (loading) return <Loading />;
  if (error || !data) return <LoadFailed error={error} onRetry={reload} />;
  const s = data.story;
  const language = byCode.get(s.language_code);
  const languageName = language?.name ?? s.language_code;
  const reviewer = user?.role === 'parent' || user?.role === 'grandparent';
  const writer = reviewer || user?.role === 'teen';
  const refresh = () => {
    reload();
    onChange();
  };

  return (
    <div className="stack">
      {s.media_asset_id && <VoicePlayer mediaAssetId={s.media_asset_id} />}
      {edit ? (
        <>
          <Field id={`tr-${id}`} label={`${languageName} transcript`}>
            <textarea id={`tr-${id}`} className="yo" {...textProps(s.language_code)} value={edit.transcript} onChange={(e) => setEdit({ ...edit, transcript: e.target.value })} />
          </Field>
          <Field id={`tl-${id}`} label="English translation">
            <textarea id={`tl-${id}`} value={edit.translation} onChange={(e) => setEdit({ ...edit, translation: e.target.value })} />
          </Field>
          <div className="row">
            <button className="btn small" onClick={async () => { await act.run(() => api(`/stories/${id}`, { method: 'PATCH', body: edit }), 'Saved — waiting for native-speaker review.'); setEdit(null); refresh(); }}>
              Save
            </button>
            <button className="btn small ghost" onClick={() => setEdit(null)}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <h4 style={{ margin: '4px 0' }}>{languageName}</h4>
              {language && <LanguageBadge language={language} />}
            </div>
            <p className="yo" {...textProps(s.language_code)} style={{ whiteSpace: 'pre-wrap' }}>{s.transcript ?? s.raw_transcript ?? <em className="muted">No transcript yet</em>}</p>
            <h4 style={{ margin: '4px 0' }}>English</h4>
            <p style={{ whiteSpace: 'pre-wrap' }}>{s.translation ?? <em className="muted">No translation yet</em>}</p>
            {s.review_status !== 'approved' && s.ai_assisted && <p className="alert safety small">Drafted with AI help. A native speaker in your family must check it before it is marked approved.</p>}
          </div>
          {writer && (
            <div className="row">
              <button className="btn small secondary" onClick={() => setEdit({ transcript: s.transcript ?? s.raw_transcript ?? '', translation: s.translation ?? '' })}>
                Edit text
              </button>
              {s.review_status !== 'approved' && (
                <button
                  className="btn small secondary"
                  disabled={act.busy}
                  onClick={async () => {
                    const r = await act.run(() => api<{ aiAvailable: boolean; message?: string }>(`/stories/${id}/process`, { method: 'POST', body: {} }));
                    if (r && !r.aiAvailable) act.setNotice(r.message ?? 'AI help is not available.');
                    else if (r) act.setNotice('AI draft ready — please check it.');
                    refresh();
                  }}
                >
                  ✨ AI: tidy & translate
                </button>
              )}
              {reviewer && s.review_status === 'native_review' && (
                <>
                  <button className="btn small" onClick={async () => { await act.run(() => api(`/stories/${id}/review`, { body: { decision: 'approve' } }), 'Approved.'); refresh(); }}>
                    ✓ Approve (I’m a native speaker)
                  </button>
                  <button className="btn small ghost" onClick={async () => { await act.run(() => api(`/stories/${id}/review`, { body: { decision: 'send_back' } })); refresh(); }}>
                    Send back
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
      <ErrorBox error={act.error} />
      <Notice text={act.notice} />
      <details>
        <summary className="small">History</summary>
        <ul className="small">
          {data.history.map((h, i) => (
            <li key={i}>
              <StatusBadge status={h.to_status} /> by {h.actor.startsWith('ai:') ? `AI (${h.actor.slice(3)})` : 'family member'} · {fmtDate(h.created_at)}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
